import { ClipboardContents, ScreenshotDisplay, ScreenshotWindow, SelectableContext } from '@renderer/chat/types'
import { FocusedWindow, HighlightContext, AppWindow } from '@highlight/common'
import { RequestType } from '@main/context/orchestrator/types/common/requests'
import { SearchTrigger } from '@main/context/orchestrator'
import IpcRouter from '@shared/ipc/IpcRouter.ts'
import type { OverlayFindInPageRequest } from '@shared/sidepanel/find-in-page'

export interface RequestContextParams {
  requestType: RequestType
  searchTrigger: SearchTrigger
}

export interface OverlayIpcRouterType {
  requestContext: (params: RequestContextParams) => Promise<HighlightContext | undefined>
  getClipboardContents: () => Promise<ClipboardContents | undefined>
  screenshotWindow: (windowTitle: string) => Promise<string>
  getActiveDisplayScreenshot: () => Promise<string | undefined>
  updateContextMacro: () => Promise<void>
  compressBase64ImageToJpeg: (params: { base64Image: string; quality?: number }) => Promise<string>
  compressBase64ImageToPng: (params: { base64Image: string; quality?: number }) => Promise<string>
  writeTextToClipboard: (data: { text: string; html?: string }) => Promise<void>
  cancelOngoingContextRequest: () => Promise<void>
  getScreenshotWindows: (includeAllWindows?: boolean) => Promise<ScreenshotWindow[]>
  getDisplayScreenshots: () => Promise<ScreenshotDisplay[]>
  getAppIcon: (appName: string) => Promise<string | null>
  requestContextForWindow: (options: { window: AppWindow | FocusedWindow }) => Promise<SelectableContext>
  muteNotificationForHours: (options: { ipcKey: string; hours: number }) => Promise<void>
  muteNotificationPermanently: (ipcKey: string) => Promise<void>
  unmuteNotification: (ipcKey: string) => Promise<void>
  closeSidepanel: () => Promise<void>
  findInPage: (request: OverlayFindInPageRequest) => Promise<boolean>
  stopFindInPage: () => Promise<void>
  typeText: (options: { text: string; pid?: number; method?: 'paste' | 'type'; html?: string }) => Promise<void>
  requestFocusedElementAttributes: () => Promise<string>
  focusHighlightWindow: () => Promise<void>
  focusWindow: (options: { title: string; pid?: number }) => Promise<boolean | void>
  toggleWindowClickThrough: (clickThrough: boolean) => Promise<void>
  requestTextAboveFocusedElement: () => Promise<string>
  getMousePosition: () => Promise<Electron.Point>
}

class OverlayIpcRouter extends IpcRouter<OverlayIpcRouterType> {
  constructor() {
    super('overlay', {
      requestContext: true,
      getClipboardContents: true,
      screenshotWindow: true,
      getActiveDisplayScreenshot: true,
      updateContextMacro: true,
      compressBase64ImageToJpeg: true,
      compressBase64ImageToPng: true,
      writeTextToClipboard: true,
      cancelOngoingContextRequest: true,
      getScreenshotWindows: true,
      getDisplayScreenshots: true,
      getAppIcon: true,
      requestContextForWindow: true,
      muteNotificationForHours: true,
      muteNotificationPermanently: true,
      unmuteNotification: true,
      closeSidepanel: true,
      findInPage: true,
      stopFindInPage: true,
      typeText: true,
      requestFocusedElementAttributes: true,
      focusHighlightWindow: true,
      focusWindow: true,
      toggleWindowClickThrough: true,
      requestTextAboveFocusedElement: true,
      getMousePosition: true,
    })
  }
}

export default OverlayIpcRouter
